<template>
	<div class="flex h-full">
		<NavigationMenu
			v-model="currentMenuValue"
			disable-click-trigger
			:delay-duration="200"
			:skip-delay-duration="300"
			class="hidden h-full lg:block"
			viewport-class="left-[-200px] bg-primary"
		>
			<NavigationMenuList class="h-[80px]" orientation="horizontal">
				<NavigationMenuItem value="profile" class="h-full">
					<NavigationMenuTrigger class="h-full">
						<PortalHeaderNavProfileOption
							v-bind="props"
							:bg-variant="bgVariant === 'primary' ? 'primary' : 'translucide'"
						/>
					</NavigationMenuTrigger>
					<NavigationMenuContent>
						<ul class="grid min-h-[123px] px-6 py-8 lg:w-[326px]">
							<li class="border-b border-primary-light-4 pb-2">
								<template v-for="option in profileOptions" :key="option.route">
									<PortalHeaderNavMobileOptionLink
										:option="option"
										class="flex items-center px-3 py-4 hover:bg-primary-light-4"
									/>
								</template>
							</li>
							<li class="pt-2">
								<PortalHeaderNavLogoutButton
									class="size-full px-3 py-4 hover:bg-primary-light-4"
									@logout="handleLogout"
								/>
							</li>
						</ul>
					</NavigationMenuContent>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>

		<PortalHeaderNavProfileOption
			v-bind="props"
			class="lg:hidden"
			@click="$emit('open-profile-menu')"
		/>
	</div>
</template>
<script setup lang="ts">
import type {
	BackgroundVariantProps,
	PortalHeaderNavLoggedInfosEmailVariantProps
} from '.';

import { useEvents } from '~/composables/events/useEvents';
import { useAuthCrossTabSync } from '~/composables/useAuthCrossTabSync';
import { useNavigationStore } from '~/store/navigationStore';
import { calculateSessionDurationFromSession } from '~~/utils';
import { storeToRefs } from 'pinia';

const { clear, session } = useUserSession();
const { notifyLogout } = useAuthCrossTabSync();
const navigationStore = useNavigationStore();
const { trackLogoutCompleted } = useEvents();

const props = defineProps<{
	variant: PortalHeaderNavLoggedInfosEmailVariantProps['variant'];
	bgVariant: BackgroundVariantProps['variant'];
}>();

defineEmits<{
	(e: 'open-profile-menu'): void;
}>();

const { profileOptions } = useHeaderNavProfile();

const currentMenuValue = ref<string>('');
const { isProfileNavigationMenuOpen } = storeToRefs(navigationStore);

// Sync component state to the store
watch(currentMenuValue, (value: string) => {
	const isProfileMenuOpen = value === 'profile';
	navigationStore.setIsProfileNavigationMenuOpen(isProfileMenuOpen);
});

// Sync store state back to the component
watch(isProfileNavigationMenuOpen, (isOpen: boolean) => {
	if (!isOpen && currentMenuValue.value === 'profile') {
		currentMenuValue.value = '';
	}
});

const handleLogout = async () => {
	// Session duration in seconds
	const sessionDuration = calculateSessionDurationFromSession(session.value);

	// Call logout API to clear server-side JWT cookies
	await $fetch('/api/_auth/logout', { method: 'POST' });

	// Clear client-side session state
	await clear();

	// Notify other tabs that user has logged out
	notifyLogout();

	trackLogoutCompleted({
		session_duration: sessionDuration
	});
};
</script>
